/* ***************************************
This mixin "exposes" the methods:
  * populateAllWorkingNikud() [for NikudEdit]
  * clearNikudEffectiveImmediately() [for NikudEdit]
  * getNewNikud() [for EditNikudPane]
  * finishEditingWordGroup() [for EditNikudPane and ManualNikudPane]
Of those, using special "dependencies" are:
  * clearNikudEffectiveImmediately() AND populateAllWorkingNikud() AND finishEditingWordGroup() all depend upon
    the wordMutatingMixins method rewriteWordOrWordsInWorkingArrayMethod()
  * finishEditingWordGroup() uses the wordMutatingMixins method spliceWordsInWorkingArrayMethod()
(all else should be considered "internal")
*************************************** */
import {onlyHebrew} from 'shared/js/commonHebrew'
import {getStandardLetterArray, getChosenWordString} from '../js/letterOperations'
import conditionalOptions from 'shared/js/conditionalOptions'
import {generateAbbreviationDisplayText} from '@/js/abbreviationDisplayText'

const DAGESH = 'ּ'

export default {
  name: 'resultsIntegrationMixins',
  computed: { // all INTERNAL
    nakdanResultsArray: function () {
      return this.$store.state.nakdanApiResponse
    },
    preSubmissionOptionsObj: function () {
      return this.$store.state.preSubmissionOptions
    }
  },
  methods: {
    reinsertChars (wordString, wordObj) { // INTERNAL
      const submissionWasJson = this.$store.state.forNakdanApiCall.jsonData
      if (submissionWasJson || this.$store.state.preSubmissionOptions.splitParens) return wordString
      const reinsertionChars = wordObj.reinsertionChars.slice()
      // console.log('wordString:')
      // console.log(wordString)
      // console.log('reinsertionChars:')
      // console.log(Object.assign({}, wordObj.reinsertionChars))
      var rewrittenString = ''
      var lettersSoFar = 0
      wordString.split('').forEach(ch => {
        if (!('ְֱֲֳִֵֶַָׇֹֻּֽׁׂ'.includes(ch))) {
          // console.log('reached letter ' + ch)
          // if (reinsertionChars.length) console.log('next charIndex: ' + reinsertionChars[0].charIndex)
          while (reinsertionChars.length && reinsertionChars[0].charIndex === lettersSoFar) {
            rewrittenString += reinsertionChars.shift().reinsertionChar
          }
          lettersSoFar += 1
        }
        rewrittenString += ch
      })
      while (reinsertionChars.length && reinsertionChars[0].charIndex === lettersSoFar) {
        rewrittenString += reinsertionChars.shift().reinsertionChar
      }
      if (reinsertionChars.length) rewrittenString += reinsertionChars.map(obj => obj.reinsertionChar).join('')
      return rewrittenString
    },
    buildExpandedOptions (segment) {
      var expandedOptions = []
      var displayTexts = []
      segment.options.forEach(opt => {
        let option = Array.isArray(opt) ? opt : [opt, ['0', '']]
        const expansion = this.reinsertChars(option[0].replace(/\|/g, ''), segment)
        const currentDisplayText = generateAbbreviationDisplayText(segment.word, expansion)
        var existingDisplayTextIndex = displayTexts.indexOf(currentDisplayText)
        if (existingDisplayTextIndex === -1) {
          displayTexts.push(currentDisplayText)
          expandedOptions.push({
            displayText: currentDisplayText,
            prefixEndIndex: option[0].indexOf('|'),
            startNextWithDagesh: option.length > 2 && option[2] // used to be: segment.fMaDagesh
          })  
        }
      })
      return expandedOptions
    },
    getNonOptionsNikudFromSource(segment, chosenNikudSource) {
      var nonOptionsNikudForWord = ''
      this.reinsertChars(segment.word, segment).split('').forEach(c => {
        if ('קראטוןםפשדגכעיחלךףזסבהנמצתץ'.includes(c)) {
          if (chosenNikudSource[0][0] !== c) {
            // console.log('letter mismatch: "' + c + '" in response !== "' + chosenNikudSource[0][0] + '"')
          } else nonOptionsNikudForWord += chosenNikudSource.shift()
        } else nonOptionsNikudForWord += c
      })
      return nonOptionsNikudForWord
    },
    getWordsArrayFromNakdanResponse (nikudType, workingSep, optionsSource, chosenNikudSource = 'first-option') {
      var wordsArray = []
      var inSurrounders = {
        parens: false,
        brackets: false,
        braces: false
      }
      var wordInSurrounders = []
      this.nakdanResultsArray.forEach(segment => {
        if (segment.sep) {
          workingSep += this.reinsertChars(segment.word, segment)
          this.updateInSurrounders(inSurrounders, this.reinsertChars(segment.word, segment)) // will only work on going "in" within THESE words
        } else {
          wordInSurrounders.push(JSON.parse(JSON.stringify(inSurrounders)))
          var nonOptionsNikudForWord
          if (chosenNikudSource !== 'first-option') {
            nonOptionsNikudForWord = this.getNonOptionsNikudFromSource(segment, chosenNikudSource)
          }
          var expandedOptions
          if (optionsSource === 'use-new-options') {
            expandedOptions = this.buildExpandedOptions(segment)
          } else if (wordsArray.length < this.workingIndices.length) {
            expandedOptions = this.$store.state.allWorkingNikud.wordsArray[this.workingIndices[wordsArray.length]].options
          } else expandedOptions = []
          const originalWord = this.reinsertChars(segment.word, segment)
          const wasAnAbbreviation = originalWord.length && (originalWord.endsWith('\'')
                                      || originalWord.slice(1, originalWord.length - 1).includes('"'))
          if (!(expandedOptions.length)) { // in the abbreviation expander, only use original word if NONE else
            const originalWordObj = {
              displayText: wasAnAbbreviation
                ? generateAbbreviationDisplayText(originalWord, originalWord)
                : originalWord,
              prefixEndIndex: -1,
            }
            if (nikudType === 'auto') {
              expandedOptions.push(originalWordObj)
            } else expandedOptions.unshift(originalWordObj)
          }
          let o = 0
          const bestOptionText = expandedOptions[o].displayText
          wordsArray.push({
            originalWord: originalWord,
            wasAnAbbreviation: wasAnAbbreviation,
            options: expandedOptions,
            choiceType: chosenNikudSource === 'first-option' ? 'option'
              : (this.waitingToRedoNikud ? 'option' : 'edited'),
            chosenOptionIndex: o,
            manualNikud: chosenNikudSource === 'first-option' ? bestOptionText
              : (this.waitingToRedoNikud ? bestOptionText : nonOptionsNikudForWord),
            editedNikud: chosenNikudSource === 'first-option' ? bestOptionText
              : (this.waitingToRedoNikud ? bestOptionText : nonOptionsNikudForWord),
            sepBefore: workingSep,
            sepAfter: '',
            wordId: chosenNikudSource === 'first-option' ? wordsArray.length
              : this.$store.state.allWorkingNikud.lastWordId + wordsArray.length + 1,
            specialData: {
              apiNonConfident: this.preSubmissionOptionsObj.showApiNonConfident && segment.fconfident === false,
              markedNonConfident: false
            },
            option1Pasuk: segment.fpasuk
          })
          if (this.preSubmissionOptionsObj.skipVoc || this.preSubmissionOptionsObj.skipPartVoc) {
            wordsArray[wordsArray.length - 1]['originalNikud'] = segment.originalNikud
          }
          workingSep = ''
        }
      })
      if (workingSep !== '') wordsArray[wordsArray.length - 1].sepAfter += workingSep
      wordsArray.forEach((wordObj, wordIndex) => {
        var userDictPosQuery, userDictNegQuery, userDictPinnedArray = []
        if (wordObj.choiceType === 'option') {
          let o = wordObj.chosenOptionIndex // i.e. zero, since there's no possibility of variation yet
          userDictPosQuery = this.localUserDictionary.lettersMap
            && this.localUserDictionary.lettersMap[wordObj.originalWord]
          if (userDictPosQuery) {
            userDictPosQuery = userDictPosQuery.filter(dictString => conditionalOptions.testConditions(
              conditionalOptions.optionObjFromDictString(dictString).conditions, wordsArray, wordIndex))
            userDictPinnedArray = userDictPosQuery.filter(
              entry => this.localUserDictionary.entryPresent[entry] === 'pinned')
          }
          userDictNegQuery = this.localUserDictionary.negativeLettersMap
            && this.localUserDictionary.negativeLettersMap[wordObj.originalWord]
          if (userDictNegQuery) userDictNegQuery = userDictNegQuery.filter(
            dictString => conditionalOptions.testConditions(
              conditionalOptions.optionObjFromDictString(dictString).conditions, wordsArray, wordIndex))
          if (!(userDictPinnedArray.length)
              && userDictNegQuery) {
            while (this.localUserDictionary.negativeEntryPresent[wordObj.options[o].displayText]
                    && o < wordObj.options.length - 1) o += 1
            if (this.localUserDictionary.negativeEntryPresent[wordObj.options[o].displayText]) {
              o = wordObj.options.map(o => o.displayText).indexOf(userDictNegQuery[0])
            }
          }
          if (o !== wordObj.chosenOptionIndex) { // i.e. it's changed b/c of negative dict query
            wordObj.chosenOptionIndex = o
            wordObj.manualNikud = wordObj.options[o].displayText
            wordObj.editedNikud = wordObj.options[o].displayText
          }
        }
        if (chosenNikudSource === 'first-option') { // because otherwise the text is from editing
          if (this.$store.state.originalDocument.source && this.$store.state.originalDocument.source.type === 'docx') {
            try {
              this.addToMap(wordObj, wordsArray.length - 1)
            }
            catch (err) {
              console.log(err.message)
            }
          }
        }
        if (chosenNikudSource === 'first-option' || this.waitingToRedoNikud) {
          const overrideNonNikudOptions = chosenNikudSource !== 'first-option'
          const shouldBeSkipped = this.ifWordShouldBeSkipped(wordObj, wordInSurrounders[wordIndex], overrideNonNikudOptions)
          if (shouldBeSkipped) {
            if (chosenNikudSource === 'first-option' && shouldBeSkipped === 'use-original-nikud') {
              wordObj.manualNikud = wordObj.originalNikud
              wordObj.choiceType = 'manual'
            } else this.clearNikudEffectiveImmediately(wordObj, -1)
          } else if (this.$store.state.preSubmissionOptions.dageshOnlyIfHeard) {
            this.clearAnyUnheardDagesh(wordObj)
          }
          if (!shouldBeSkipped) {
            if (userDictPosQuery && userDictPosQuery.length 
                && (userDictPinnedArray.length
                    || (this.localUserDictionary.negativeEntryPresent
                        && this.localUserDictionary.negativeEntryPresent[wordObj.options[0].displayText]))) {
              wordObj.manualNikud = conditionalOptions.optionObjFromDictString(
                userDictPinnedArray.length ? userDictPinnedArray[0] : userDictPosQuery[0]).displayText
              wordObj.choiceType = 'manual'
            }
          }
        }
        wordObj.altered = chosenNikudSource === 'first-option' ? false // for now -- even if user dict was used
          : true // because that means it resulted from editing
      })
      /* if (this.$store.state.originalDocument.source && this.$store.state.originalDocument.source.type === 'docx') {
        console.log('final wordToRunAndPosMap:')
        console.log(this.$store.state.originalDocument.source.wordToRunAndPosMap)  
      } */
      return wordsArray
    },
    populateAllWorkingNikud () {
      const nikudType = this.$store.state.preSubmissionOptions.nikudType
      const workingSep = ''
      const wordsArray = this.getWordsArrayFromNakdanResponse(nikudType, workingSep, 'use-new-options')
      this.$store.commit('STORE_ALL_WORKING_NIKUD', {
        contextData: { wordIndex: 0, letterIndex: 0 }, // for undo
        newAllWorkingNikudJson: JSON.stringify({ wordsArray: wordsArray, lastWordId: wordsArray.length - 1 })
      })
      const whetherToMove = wordsArray.length // if no words (somehow), nothing to auto-select
      return whetherToMove
    },
    addToMap (wordObj, wordIndex) {
      var mapSoFar = this.$store.state.originalDocument.source.wordToRunAndPosMap
      var runArray = this.$store.state.originalDocument.source.runArray
      var currentRunIndex = 0
      var currentRunPos = 0
      if (mapSoFar.length) {
        currentRunIndex = mapSoFar[mapSoFar.length - 1].runIndex
        currentRunPos = mapSoFar[mapSoFar.length - 1].endPos + 1
      }
      var mapToAdd = { wordIndex: wordIndex, runIndex: null, startPos: null, endPos: null }
      for (var charInWordObj = 0; charInWordObj < wordObj.originalWord.length; charInWordObj++) {
        var currentLetter = wordObj.originalWord[charInWordObj]
        var runAndPosFound = false
        var ranOutOfRuns = false
        while (!runAndPosFound && !ranOutOfRuns) {
          if (currentRunPos > runArray[currentRunIndex].length - 1) {
            currentRunPos = 0
            currentRunIndex += 1
            if (currentRunIndex > runArray.length - 1) {
              ranOutOfRuns = true
            }
          }
          if (!ranOutOfRuns) {
            if (runArray[currentRunIndex][currentRunPos] === currentLetter) {
              runAndPosFound = true
            } else currentRunPos += 1
          }
        }
        if (ranOutOfRuns) {
          // console.log('Ran out of runs for char "' + currentLetter + '" of word "' + wordObj.originalWord + '"')
        } else {
          if (charInWordObj === 0) {
            mapToAdd.runIndex = currentRunIndex
            mapToAdd.startPos = currentRunPos
          }
          if (charInWordObj === wordObj.originalWord.length - 1) mapToAdd.endPos = currentRunPos
          currentRunPos += 1 // prevent using the same "pos" twice in a row just because the letters are the same
        }
      }
      // console.log('adding to mapSoFar:')
      // console.log(mapToAdd)
      this.$store.commit('REWRITE_WORD_TO_RUN_AND_POS_MAP', mapSoFar.concat([mapToAdd]))
    },
    ifWordShouldBeSkipped (wordObj, inSurrounders, overrideNonNikudOptions = false) {
      var shouldBeSkipped = false
      let options = this.preSubmissionOptionsObj
      if (options.nikudType === 'manual' && !overrideNonNikudOptions) {
        shouldBeSkipped = true
      } else if (options.skipLetters && wordObj.originalWord.length === 1) {
        shouldBeSkipped = true
      } else if (options.skipAbbrevs && wordObj.originalWord.includes('"')) {
        shouldBeSkipped = true
      } else if (options.skipParens && inSurrounders.parens) {
        shouldBeSkipped = true
      } else if (options.skipBrackets && inSurrounders.brackets) {
        shouldBeSkipped = true
      } else if (options.skipBraces && inSurrounders.braces) {
        shouldBeSkipped = true
      } else if (options.skipVoc && !overrideNonNikudOptions
          && wordObj.originalNikud.search(/[ְֱֲֳִֵֶַָׇֹֻּׁׂ]/) !== -1 && wordObj.originalNikud.replace(/[^א-ת]/g, '').length - wordObj.originalNikud.replace(/[^ְֱֲֳִֵֶַָׇֹֻּׁׂ]/g, '').length < 3) {
        shouldBeSkipped = 'use-original-nikud'
      } else if (options.skipPartVoc && !overrideNonNikudOptions
          && wordObj.originalNikud.search(/[ְֱֲֳִֵֶַָׇֹֻּׁׂ]/) !== -1 && wordObj.originalNikud.replace(/[^א-ת]/g, '').length - wordObj.originalNikud.replace(/[^ְֱֲֳִֵֶַָׇֹֻּׁׂ]/g, '').length > 2) {
        shouldBeSkipped = 'use-original-nikud'
      }
      return shouldBeSkipped
    },
    updateInSurrounders (inSurrounders, sep) {
      if (inSurrounders.parens) {
        if (/\)(?!.*\()/.test(sep)) { // if there's a "close parens" NOT followed by an "open parens"
          inSurrounders.parens = false
        }
      } else if (/\((?!.*\))/.test(sep)) { // if there's an "open parens" NOT followed by a "close parens"
        inSurrounders.parens = true
      }
      if (inSurrounders.brackets) {
        if (/\](?!.*\[)/.test(sep)) { // if there's a "close brackets" NOT followed by an "open brackets"
          inSurrounders.brackets = false
        }
      } else if (/\[(?!.*\])/.test(sep)) { // if there's an "open brackets" NOT followed by a "close brackets"
        inSurrounders.brackets = true
      }
      if (inSurrounders.braces) {
        if (/\}(?!.*\{)/.test(sep)) { // if there's a "close braces" NOT followed by an "open braces"
          inSurrounders.braces = false
        }
      } else if (/\{(?!.*\})/.test(sep)) { // if there's an "open braces" NOT followed by a "close braces"
        inSurrounders.braces = true
      }
    },
    getNewNikud () { // simplified version of populateAllWorkingNikud(), just to get "options" arrays
      var wordsArray = []
      this.nakdanResultsArray.forEach(segment => {
        if (!(segment.sep)) {
          var expandedOptions = this.buildExpandedOptions(segment)
          const originalWord = this.reinsertChars(segment.word, segment)
          if (!(expandedOptions.map(o => o.displayText).includes(originalWord))) {
            expandedOptions.push({
              displayText: originalWord,
              prefixEndIndex: -1
            })
          }
          wordsArray.push({
            options: expandedOptions
          })
        }
      })
      return wordsArray
    },
    finishEditingWordGroup (optionsSource = 'use-new-options') {
      if (this.whitespaceAtStartOfWorkingWord) this.workingWord = this.whitespaceAtStartOfWorkingWord + this.workingWord
      if (!(this.workingWord)) return
      var lettersOfWordGroup = onlyHebrew(this.workingWord).split(/(?=[א-ת])/) // adapted from getStandardLetterArray
      const workingSep = this.workingWord.slice(0, this.workingWord.indexOf(this.workingWord.trim()))
      const nikudType = 'auto'
      var newWordsArray = this.getWordsArrayFromNakdanResponse(nikudType, workingSep, optionsSource, lettersOfWordGroup)
      if (this.workingIndices.length === newWordsArray.length) {
        newWordsArray.forEach((newWord, w) => {
          const originalWordObj = this.$store.state.allWorkingNikud.wordsArray[this.workingIndices[w]]
          const earlierWord = originalWordObj.originalWord
          if (!(newWord.options.map(o => o.displayText).includes(earlierWord))) {
            newWord.options.push({
              displayText: earlierWord,
              prefixEndIndex: -1
            })
          }
          if (Object.prototype.hasOwnProperty.call(originalWordObj, 'previousOriginalWord')) {
            originalWordObj.previousOriginalWord.forEach((evenEarlierWord) => {
              if (!(newWord.options.map(o => o.displayText).includes(evenEarlierWord))) {
                newWord.options.push({
                  displayText: evenEarlierWord,
                  prefixEndIndex: -1
                })
              }
            })
            newWord['previousOriginalWord'] = originalWordObj.previousOriginalWord
          } else newWord['previousOriginalWord'] = []
          newWord.previousOriginalWord.push(earlierWord)
        })
      }
      const before = this.workingIndices.map(i => {
        const wordObj = this.$store.state.allWorkingNikud.wordsArray[i]
        // console.log('wordObj ' + i + ': ' + JSON.stringify(wordObj))
        return wordObj.sepBefore + getChosenWordString(wordObj) + wordObj.sepAfter
      }).join(',')
      const after = newWordsArray.map(wordObj => wordObj.sepBefore + getChosenWordString(wordObj) + wordObj.sepAfter).join('')
      const beforeLengths = this.workingIndices.map(i => {
        const wordObj = this.$store.state.allWorkingNikud.wordsArray[i]
        return wordObj.sepBefore.length + '-' + getChosenWordString(wordObj).length + '-' + wordObj.sepAfter.length
      }).join(',')
      const afterLengths = newWordsArray.map(wordObj => wordObj.sepBefore.length + '-' + getChosenWordString(wordObj).length + '-' + wordObj.sepAfter.length).join(',')
      // console.log('before:')
      // console.log(before)
      // console.log('after')
      // console.log(after)
      const currentLetterIndex = this.$store.state.currentPositionData.letterIndex
      const currentWordIndex = this.$store.state.currentPositionData.wordIndex
      const indexOfCurrentWordIndexInWorkingIndices = this.workingIndices.indexOf(currentWordIndex)
      const currentWordOptionsBefore = this.$store.state.allWorkingNikud.wordsArray[currentWordIndex].options.map(
        o => o.displayText
      )
      const currentWordOptionsAfter = (indexOfCurrentWordIndexInWorkingIndices !== -1 
                                        && indexOfCurrentWordIndexInWorkingIndices < newWordsArray.length) ?
                                            newWordsArray[indexOfCurrentWordIndexInWorkingIndices].options.map(
                                              o => o.displayText
                                            ) : null
      if (before !== after || beforeLengths !== afterLengths // only do a commit if "undo"ing it will be visible
          || currentWordOptionsAfter && currentWordOptionsAfter !== currentWordOptionsBefore) { 
        this.spliceWordsInWorkingArrayMethod({
          contextData: { // for undo
            wordIndex: currentWordIndex,
            letterIndex: currentLetterIndex
          },
          spliceWordsContainer: {
            startIndex: this.workingIndices[0], toDelete: this.workingIndices.length, spliceWordsArray: newWordsArray
          }
        })
      }
      this.waitingToRedoNikud = false
      this.editNikudProps.showEditNikud = false
      const currentWord = this.$store.state.allWorkingNikud.wordsArray[currentWordIndex]
      if (!currentWord) {
        if (this.$store.state.allWorkingNikud.wordsArray.length) {
          this.$store.commit('SET_WORD_AND_LETTER', {
            wordIndex: this.workingIndices[0],
            letterIndex: this.$store.state.allWorkingNikud.wordsArray[this.workingIndices[0]].sepBefore.length
          })
        } else this.$store.commit('SET_WORD_AND_LETTER', { wordIndex: -1, letterIndex: -1 })
      } 
      return
    },
    clearAnyUnheardDageshUsingLetterArray (letterArray) {
      let rewritten = ''
      letterArray.forEach((le) => {
        if (!(['פ', 'ב', 'כ', 'ו'].includes(le[0]))) { 
          rewritten += le.replace(DAGESH, '')
        } else rewritten += le
      })
      return rewritten
    },
    clearAnyUnheardDagesh (wordObj) { // note: wordObj must NOT be in the store! (currently)
      let originalChoiceType = wordObj.choiceType
      wordObj.choiceType = 'manual'
      let originalManualNikudLetters = getStandardLetterArray(getChosenWordString(wordObj))
      wordObj.options.forEach(option => {
        wordObj.manualNikud = option.displayText
        option.displayText = this.clearAnyUnheardDageshUsingLetterArray(getStandardLetterArray(getChosenWordString(wordObj)))
      })
      wordObj.manualNikud = this.clearAnyUnheardDageshUsingLetterArray(originalManualNikudLetters)
      wordObj.choiceType = originalChoiceType
    },
    clearNikudEffectiveImmediately (currentWord, indexInAllWorkingNikud) { // here b/c currently only used by NikudEdit
        const oldChosenWord = getChosenWordString(currentWord)
        const chosenWord = generateAbbreviationDisplayText(currentWord.originalWord, currentWord.originalWord) // in abbreviation, w/o "nikud" = w/o EXPANSION
        if (chosenWord !== oldChosenWord) {
          if (indexInAllWorkingNikud === -1) { // i.e. goal's to change word obj directly b/c it's not in "store"
            currentWord.editedNikud = chosenWord
            currentWord.manualNikud = chosenWord
            if (currentWord.choiceType === 'option') currentWord.choiceType = 'manual'
            currentWord.altered = true
          } else {
            var newWordContainer = {
              index: indexInAllWorkingNikud, changes: { 
                altered: true,
                editedNikud: chosenWord,
                manualNikud: chosenWord
              }
            }
            if (currentWord.choiceType === 'option') newWordContainer.changes["choiceType"] = 'manual'
            const currentWordIndex = this.$store.state.currentPositionData.wordIndex
            this.rewriteWordOrWordsInWorkingArrayMethod({
              contextData: { // for undo
                wordIndex: currentWordIndex,
                letterIndex: this.$store.state.currentPositionData.letterIndex },
              newWordContainer: newWordContainer
            })
            if (indexInAllWorkingNikud === currentWordIndex) {
              this.$store.commit('SET_CURRENT_LETTER_INDEX', currentWord.sepBefore.length)
            }
          }
        }
      }
    }
  }
